import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import { MdChevronRight, MdExpandMore, MdEdit } from 'react-icons/md';

import { GroupSku, Sku, SkuGroupLink } from '@app/api/models/sku';
import { Dialog, Paper, Txt } from '@app/components/common';
import { booleanBodyRender, TableColumn } from '@app/components/common/table';
import { TableBase } from '@app/components/common/table/table-base';
import { useAlert, useApi } from '@app/hooks';

import { SkuForm } from './form';
import { SkuLookups, SkuViewMode } from './types';
import { SkuViewModeToolbar } from './view-mode-toolbar';

type GroupNode = GroupSku & {
  children: GroupNode[];
};

const buildGroupTree = (groups: GroupSku[]): GroupNode[] => {
  const byId = new Map<number, GroupNode>();
  groups.forEach((group) => {
    byId.set(group.id, { ...group, children: [] });
  });

  const roots: GroupNode[] = [];
  byId.forEach((node) => {
    const parentId = node.group_id;
    if (parentId && byId.has(parentId) && parentId !== node.id) {
      byId.get(parentId)!.children.push(node);
      return;
    }
    roots.push(node);
  });

  const sortNodes = (nodes: GroupNode[]) => {
    nodes.sort((a, b) => a.title.localeCompare(b.title, 'ru'));
    nodes.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);
  return roots;
};

const TREE_INDENT = 18;
const TREE_ROW_HEIGHT = 34;

const GroupTreeItem: FC<{
  node: GroupNode;
  depth: number;
  isLast: boolean;
  /** guides[i] = true — на уровне i рисуем вертикаль дальше вниз */
  guides: boolean[];
  selectedId?: number;
  expandedIds: Set<number>;
  onToggle: (id: number) => void;
  onSelect: (node: GroupNode) => void;
}> = ({
  node,
  depth,
  isLast,
  guides,
  selectedId,
  expandedIds,
  onToggle,
  onSelect,
}) => {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedId === node.id;

  return (
    <Box position="relative">
      <Flex
        alignItems="center"
        h={`${TREE_ROW_HEIGHT}px`}
        pr={2}
        pl={`${8 + depth * TREE_INDENT}px`}
        cursor="pointer"
        position="relative"
        bg={isSelected ? 'brand.50' : 'transparent'}
        color={isSelected ? 'brand.700' : undefined}
        _hover={{ bg: isSelected ? 'brand.50' : 'gray.50' }}
        onClick={() => onSelect(node)}
      >
        {Array.from({ length: depth }).map((_, level) => {
          const left = 8 + level * TREE_INDENT + 11;
          const isBranchLevel = level === depth - 1;
          const drawVertical = isBranchLevel || guides[level];

          return (
            <Box
              key={level}
              position="absolute"
              left={`${left}px`}
              top={0}
              bottom={0}
              w="12px"
              pointerEvents="none"
            >
              {drawVertical && (
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  w="1px"
                  bg="gray.300"
                  h={isBranchLevel && isLast ? '50%' : '100%'}
                />
              )}
              {isBranchLevel && (
                <Box
                  position="absolute"
                  left={0}
                  top="50%"
                  w="10px"
                  h="1px"
                  bg="gray.300"
                />
              )}
            </Box>
          );
        })}

        {hasChildren ? (
          <IconButton
            aria-label={isExpanded ? 'collapse' : 'expand'}
            size="xs"
            variant="ghost"
            minW="24px"
            h="24px"
            icon={isExpanded ? <MdExpandMore /> : <MdChevronRight />}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(node.id);
            }}
          />
        ) : (
          <Box width="24px" flexShrink={0} />
        )}
        <Txt
          fontSize="sm"
          fontWeight={isSelected ? 600 : 400}
          title={node.title}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {node.title}
        </Txt>
      </Flex>

      {hasChildren && isExpanded && node.children.map((child, index) => (
        <GroupTreeItem
          key={child.id}
          node={child}
          depth={depth + 1}
          isLast={index === node.children.length - 1}
          guides={[...guides, !isLast]}
          selectedId={selectedId}
          expandedIds={expandedIds}
          onToggle={onToggle}
          onSelect={onSelect}
        />
      ))}
    </Box>
  );
};

const useCatalogColumns = ({
  lookups,
  onEdit,
  canChange,
}: {
  lookups: SkuLookups;
  onEdit: (sku: Sku) => void;
  canChange: boolean;
}): TableColumn<Sku>[] => [
  {
    name: 'id',
    label: '#',
    options: { filter: false, sort: false },
  },
  {
    name: 'title',
    label: 'Наименование',
    options: { filter: false, sort: false },
  },
  {
    name: 'code',
    label: 'Код',
    options: { filter: false, sort: false },
  },
  {
    name: 'brand',
    label: 'Бренд',
    options: { filter: false, sort: false },
  },
  {
    name: 'category_id',
    label: 'Категория',
    options: {
      filter: false,
      sort: false,
      customBodyRender: (value: number) => (
        <span>{lookups.categoriesById[value] || '—'}</span>
      ),
    },
  },
  {
    name: 'is_active',
    label: 'Активен',
    options: {
      filter: false,
      sort: false,
      customBodyRender: booleanBodyRender(),
    },
  },
  ...(canChange
    ? ([
      {
        name: 'id',
        label: 'Действия',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_id, { rowData }) => (
            <Tooltip label="Редактировать">
              <IconButton
                name="edit"
                aria-label="edit"
                size="sm"
                onClick={() => onEdit(rowData as Sku)}
              >
                <MdEdit />
              </IconButton>
            </Tooltip>
          ),
        },
      },
    ] as TableColumn<Sku>[])
    : []),
];

export const SkuCatalog: FC<{
  lookups: SkuLookups;
  canChange: boolean;
  mode: SkuViewMode;
  onModeChange: (mode: SkuViewMode) => void;
}> = ({ lookups, canChange, mode, onModeChange }) => {
  const api = useApi();
  const { error } = useAlert();

  const [groups, setGroups] = useState<GroupSku[]>([]);
  const [links, setLinks] = useState<SkuGroupLink[]>([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [skusLoading, setSkusLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupSku>();
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [skus, setSkus] = useState<Sku[]>([]);
  const [selected, setSelected] = useState<Partial<Sku>>();
  const [open, setOpen] = useState(false);

  const tree = useMemo(() => buildGroupTree(groups), [groups]);

  const loadCatalog = useCallback(async () => {
    setTreeLoading(true);
    try {
      const [groupsRes, linksRes] = await Promise.all([
        api.sku.getGroups({ is_active: true }),
        api.sku.getSkuGroupLinks(true),
      ]);
      const nextGroups = groupsRes?.data || [];
      setGroups(nextGroups);
      setLinks((linksRes?.data || []).filter((item) => item.is_active));
      setExpandedIds(new Set(nextGroups.filter((item) => !item.group_id).map((item) => item.id)));
    } catch (e) {
      error(e.message || 'Не удалось загрузить каталог групп');
    } finally {
      setTreeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const [skuCache, setSkuCache] = useState<Record<number, Sku>>();

  const fetchSkuCache = useCallback(async () => {
    const all: Sku[] = [];
    let offset = 0;
    let total = Infinity;

    while (offset < total) {
      const response = await api.sku.getSkus({
        limit: 500,
        offset,
        sort: '+id',
        filter: {},
      });
      const page = response?.data?.data || [];
      total = response?.data?.total ?? page.length;
      all.push(...page);
      if (!page.length) {
        break;
      }
      offset += 500;
    }

    const nextCache = all.reduce<Record<number, Sku>>((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
    setSkuCache(nextCache);
    return nextCache;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadGroupSkus = useCallback(async (groupId: number, forceRefresh = false) => {
    const skuIds = links
      .filter((item) => item.group_id === groupId)
      .map((item) => item.sku_id);

    if (!skuIds.length) {
      setSkus([]);
      return;
    }

    setSkusLoading(true);
    try {
      const cache = !forceRefresh && skuCache
        ? skuCache
        : await fetchSkuCache();
      setSkus(
        skuIds
          .map((id) => cache[id])
          .filter((item): item is Sku => !!item)
          .sort((a, b) => a.title.localeCompare(b.title, 'ru'))
      );
    } catch (e) {
      error(e.message || 'Не удалось загрузить SKU группы');
      setSkus([]);
    } finally {
      setSkusLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links, skuCache, fetchSkuCache]);

  useEffect(() => {
    if (!selectedGroup) {
      setSkus([]);
      return;
    }
    loadGroupSkus(selectedGroup.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup?.id, links]);

  const onToggleExpand = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const onEdit = useCallback((sku: Sku) => {
    setSelected(sku);
    setOpen(true);
  }, []);

  const columns = useCatalogColumns({ lookups, onEdit, canChange });

  return (
    <>
      <Flex
        gap={3}
        alignItems="stretch"
        minH="70vh"
        direction={{ base: 'column', md: 'row' }}
      >
        <Paper>
          <Box
            width={{ base: '100%', md: '300px' }}
            h={{ base: '40vh', md: '70vh' }}
            display="flex"
            flexDirection="column"
            overflow="hidden"
          >
            <Box px={4} py={3} borderBottomWidth="1px" borderColor="gray.100" flexShrink={0}>
              <Txt fontWeight={600}>Каталог групп</Txt>
            </Box>
            <Box flex="1" minH={0} overflowY="auto" overflowX="hidden" py={1}>
              {treeLoading && (
                <Flex justifyContent="center" alignItems="center" minH="200px">
                  <Spinner />
                </Flex>
              )}
              {!treeLoading && !tree.length && (
                <Txt px={4} color="gray.500">Нет активных групп</Txt>
              )}
              {!treeLoading && tree.map((node, index) => (
                <GroupTreeItem
                  key={node.id}
                  node={node}
                  depth={0}
                  isLast={index === tree.length - 1}
                  guides={[]}
                  selectedId={selectedGroup?.id}
                  expandedIds={expandedIds}
                  onToggle={onToggleExpand}
                  onSelect={setSelectedGroup}
                />
              ))}
            </Box>
          </Box>
        </Paper>

        <Box flex={1} minW={0}>
          <TableBase<Sku>
            title={
              <Flex gap={2} alignItems="center">
                <Txt>Управление SKU</Txt>
                <SkuViewModeToolbar mode={mode} onChange={onModeChange} />
              </Flex>
            }
            secondaryToolbar={
              <Txt color="gray.600">
                {selectedGroup
                  ? `Группа: ${selectedGroup.title}`
                  : 'Выберите группу в каталоге слева'}
              </Txt>
            }
            columns={columns}
            data={skus}
            loading={skusLoading}
            paper
          />
        </Box>
      </Flex>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={selected?.id ? 'Редактирование SKU' : 'Создание SKU'}
        width={720}
        footerless
      >
        {open && (
          <SkuForm
            initial={selected}
            lookups={lookups}
            onSubmit={() => {
              setOpen(false);
              if (selectedGroup) {
                loadGroupSkus(selectedGroup.id, true);
              }
            }}
            onCancel={() => setOpen(false)}
          />
        )}
      </Dialog>
    </>
  );
};

export default SkuCatalog;
